import type { OrderDetails } from '@/entities/order/model'
import { useCallback } from 'react'
import { useStateConfigQuery, useZebraBarcodeEnabled } from '@/entities/state/model'
import { isAllCratesProcessed, isZebraScanProgressStatus } from './order.zebraScan'

/**
 * Shown when a completion is blocked because crates are still unaccounted for.
 * Shared by every route that can complete an order so the wording cannot drift.
 */
export const CRATE_SCAN_PENDING_MESSAGE
  = 'Scan all crates or mark them missing before completing this order'

/**
 * Whether an order still has crates to account for before it may be completed.
 *
 * Crate scanning is resolved *before* the completion moment — every expected crate
 * either scanned or its type marked missing — and only then does completing the
 * order trigger anything else. The backend enforces this too
 * (`validatePersistedCrateScanForCompletion`), but by then the team member has
 * already been walked through a flow they were never eligible to start.
 */
export function useCrateScanGate() {
  const zebraBarcodeEnabled = useZebraBarcodeEnabled()
  const { data: stateConfig } = useStateConfigQuery()

  /**
   * True while expected crates are neither scanned nor marked missing.
   *
   * Only applies while the order is in a scan-progress status and Zebra scanning is
   * enabled for the store — outside that window there is nothing to account for.
   */
  const isCrateScanPending = useCallback(
    (order?: OrderDetails): boolean => {
      if (!order || !zebraBarcodeEnabled) {
        return false
      }
      if (!isZebraScanProgressStatus(order.status, stateConfig?.states)) {
        return false
      }
      return !isAllCratesProcessed(order)
    },
    [zebraBarcodeEnabled, stateConfig?.states],
  )

  return { isCrateScanPending }
}
